// Shared types + display metadata for the orchestration dashboard.
// Mirrors docs/api/contract.md — keep in sync.

export const STAGES = [
  "refine",
  "analyze",
  "engineer",
  "functionality_review",
  "design_review",
  "peer_review",
  "security_review",
  "finalize",
] as const;

export type Stage = (typeof STAGES)[number];

export const STAGE_LABEL: Record<Stage, string> = {
  refine: "Refine",
  analyze: "Analyze",
  engineer: "Engineer",
  functionality_review: "Functionality Review",
  design_review: "Design Review",
  peer_review: "Peer Review",
  security_review: "Security Review",
  finalize: "Finalize",
};

// Color per stage — reused by the chart, legends, and config dots.
export const STAGE_COLOR: Record<Stage, string> = {
  refine: "#94a3b8",
  analyze: "#2563eb",
  engineer: "#16a34a",
  functionality_review: "#9333ea",
  design_review: "#db2777",
  peer_review: "#8b5cf6",
  security_review: "#dc2626",
  finalize: "#0891b2",
};

export type RunStatus =
  | "pending"
  | "running"
  | "needs_rework"
  | "completed"
  | "failed";

export interface Run {
  id: string;
  storySlug: string;
  title: string;
  status: RunStatus;
  iterations: number;
  prUrl: string | null;
  headBranch: string | null; // branch the PR was opened from (set with prUrl)
  targetRepo: string | null; // repo this run targets (from the work order)
  domain: string | null; // business/technical domain (from the work order)
  triggeredBy: string | null; // who triggered the run (e.g. approved the work order); null = machine/ad-hoc
  createdAt: string;
  completedAt: string | null;
}

// Status of the GitHub App integration (admin panel). No secrets.
export interface GitHubAppStatus {
  configured: boolean; // GH_APP_ID + GH_APP_RW_KEY set
  verified: boolean; // credentials accepted by GitHub
  appId: string | null;
  appSlug: string | null;
  error: string | null;
}

export type LogLevel = "info" | "warn" | "error";

export interface StageLog {
  id: number;
  runId: string;
  stage: Stage;
  level: LogLevel;
  message: string;
  timestamp: string;
}

export type Verdict = "pass" | "fail" | null;

export interface StageMetric {
  id: number;
  runId: string;
  stage: Stage;
  model: string;
  agentLabel: string | null;
  durationMs: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number; // catalog list price × tokens
  actualCostUsd: number | null; // real billed cost from a real runner (null = simulated)
  verdict: Verdict;
  timestamp: string;
}

export interface StageConfig {
  stage: Stage | string;  // Stage for pipeline stages; string for service configs (e.g. "co_engineer")
  model: string;
  updatedAt: string;
}

export const CO_ENGINEER_STAGE = "co_engineer";

export type Provider = "api" | "local";
export type ApiProvider = "anthropic" | "openai" | "grok" | "other";

export interface ModelCatalogEntry {
  id: string;
  name: string;
  provider: Provider;
  apiProvider: ApiProvider | null;
  baseUrl: string | null;
  costPerMillionTokensIn: number;
  costPerMillionTokensOut: number;
  tier: string | null;
  notes: string | null;
  createdAt: string;
}

export type SystemReviewStatus = "pending" | "completed" | "failed";

export interface SystemReview {
  id: string;
  status: SystemReviewStatus;
  runId: string | null;
  stage: string | null;
  model: string | null;
  from: string | null;
  to: string | null;
  reviewModel: string;
  requestedBy: string | null;
  result: string | null;
  error: string | null;
  createdAt: string;
  completedAt: string | null;
}

// Work order — placed by the brain app via webhook; gated by human approval
// before the orchestrator activates a task/VM.
export type WorkOrderStatus =
  | "pending"
  | "activating"
  | "active"
  | "rejected"
  | "failed";

export interface WorkOrder {
  id: string;
  source: string;
  title: string;
  slug: string;
  requirement: string | null;
  storyPath: string | null;
  sourceRef: string | null;
  domain: string | null;
  targetRepo: string | null;
  status: WorkOrderStatus;
  taskSlug: string | null;
  decidedBy: string | null;
  note: string | null;
  createdAt: string;
  decidedAt: string | null;
}

export interface FileChange {
  path: string;
  status: "A" | "M" | "D";
  content?: string;      // final file text (A/M)
  oldContent?: string;   // original file text (M/D) — present when agent supplies it
}

// Brain ingestion run types (distinct from the orchestration Run above).

export type BrainRunStatus = "Running" | "Succeeded" | "Failed" | "Cancelled";

export interface BrainRun {
  id: number;
  status: BrainRunStatus;
  trigger: string;
  tenant: string;
  workflow: string;
  runGroupId: string | null;
  triggeredByEmail: string | null;
  triggeredByName: string | null;
  docsModel: string | null;
  requirementsModel: string | null;
  startedAt: string;
  finishedAt: string | null;
  rawFilesRead: string[];
  filesCreated: string[];
  filesModified: string[];
  filesDeleted: string[];
  summary: string | null;
  error: string | null;
  inputTokens: number;
  outputTokens: number;
  cacheCreationTokens: number;
  cacheReadTokens: number;
  billingWeight: number;
}

export interface NoopResult {
  noop: true;
  reason: string;
}

export interface Domain {
  id: number;
  name: string;
  description: string | null;
  repos: string[];
  createdAt: string;
  updatedAt: string;
}

export interface DomainInput {
  name: string;
  description?: string;
  repos: string[];
}

export interface TranscriptSummary {
  id: number;
  domainId: number;
  fileName: string;
  sizeBytes: number;
  uploadedByEmail: string | null;
  uploadedAt: string;
}

export interface Requirement {
  id: number;
  runId: number;
  title: string;
  body: string;
  repo: string | null;
  sourceRefs: string[];
  status: string;
  webhookSentAt: string | null;
  createdAt: string;
  dedupKey: string;
}

export interface BrainConfig {
  id: number;
  docsModel: string | null;
  requirementsModel: string | null;
  updatedAt: string;
}

export interface WikiDoc {
  section: string;
  name: string;
  path: string;
  lastModified: string | null;
}

// Which value the chart plots per stage.
export type MetricKind = "tokens" | "cost" | "duration";

export const tokensOf = (m: StageMetric | BrainRun): number => {
  if ("tokensIn" in m) return m.tokensIn + m.tokensOut;
  return m.inputTokens + m.outputTokens + m.cacheCreationTokens + m.cacheReadTokens;
};
// Brain run metric/display utilities.
export type RunMetricKind = "duration" | "tokens" | "billing";

export const RUN_STATUS_COLOR: Record<BrainRunStatus, string> = {
  Running: "#326CE5",
  Succeeded: "#16a34a",
  Failed: "#dc2626",
  Cancelled: "#94a3b8",
};

export const durationMsOf = (r: BrainRun): number => {
  if (!r.finishedAt) return 0;
  return new Date(r.finishedAt).getTime() - new Date(r.startedAt).getTime();
};

export const filesChangedOf = (r: BrainRun): number =>
  r.filesCreated.length + r.filesModified.length + r.filesDeleted.length;

export const formatNumber = (n: number): string =>
  n >= 1_000_000
    ? `${(n / 1_000_000).toFixed(1)}M`
    : n >= 1_000
      ? `${(n / 1_000).toFixed(1)}k`
      : `${n}`;

export const formatBilling = (w: number): string =>
  w === 0 ? "0" : w < 0.01 ? w.toFixed(4) : w.toFixed(2);

export const formatSeconds = (ms: number): string => {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

export const valueOf = (m: StageMetric, kind: MetricKind): number => {
  if (kind === "tokens") return tokensOf(m);
  if (kind === "cost") return m.costUsd ?? 0;
  return m.durationMs;
};

export const formatCost = (usd: number): string => {
  if (usd === 0) return "$0.00";
  if (usd < 0.01) return `$${usd.toFixed(4)}`;
  return `$${usd.toFixed(2)}`;
};

// A color for a series — engineer fan-out agents get distinct shades of the
// engineer color so parallel agents read as separate lines.
const ENGINEER_SHADES = ["#15803d", "#16a34a", "#22c55e", "#4ade80", "#86efac"];
export function seriesColor(stage: Stage, agentLabel: string | null): string {
  if (stage === "engineer" && agentLabel) {
    const n = parseInt(agentLabel.replace(/\D/g, ""), 10);
    if (!Number.isNaN(n) && n > 0) {
      return ENGINEER_SHADES[(n - 1) % ENGINEER_SHADES.length];
    }
  }
  return STAGE_COLOR[stage];
}
